import { Box, Flex, VStack } from "@chakra-ui/layout"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"

import { HasDisplay } from "~/@shared/api-types/id-name-display"
import FunctionLink from "~/components/atoms/FunctionLink"
import { SpacingSize } from "~/consts/style-mixins"

import ErrorText from "./ErrorText"
import FormListEnteredItem from "./FormListEnteredItem"
import { InputAutoSuggest, InputAutoSuggestProps } from "./InputAutoSuggest"

type PickedInputAutoSuggestProps<Suggest> = Pick<
  InputAutoSuggestProps<Suggest>,
  "id" | "placeholder" | "suggestions" | "getter"
>

type Props<Suggest> = PickedInputAutoSuggestProps<Suggest> & {
  defaultValue?: string[]
  onChange?: (value: string[]) => void
  errorMessage?: string
  /** 一覧をリストではなく、タグ形式で表示する */
  isTagStyle?: boolean
  /**
   * @returns
   * - エラーがあるならメッセージをreturn
   * - エラーがない時
   *   - ブロックがあるときは何もreturnしない
   * - 以下のような時は失敗時`|| MESSAGE`で
   * ```tsx
   *  <InputAutoSuggestMultiple validate={(it) => it.length<=64||"64文字以内で入力してください"} />
   * ```
   */
  validate?: (_: string) => string | undefined | true
}

export function InputAutoSuggestMultiple<Suggest = HasDisplay>(_props: Props<Suggest>) {
  const { defaultValue, onChange, errorMessage: _errorMessage, isTagStyle, validate, ...props } = _props

  const [skillText, setSkillText] = useState("")
  const [skills, setSkills] = useState<string[]>(defaultValue ?? [])
  const [localErrorMessage, setLocalErrorMessage] = useState("")

  const errorMessage = useMemo(() => _errorMessage || localErrorMessage, [_errorMessage, localErrorMessage])

  const addSkill = useCallback((skill: string) => {
    setLocalErrorMessage("")
    if (!skill) {
      return
    }
    const maybeErrorMessage = validate?.(skill)
    if (typeof maybeErrorMessage === "string") {
      setLocalErrorMessage(maybeErrorMessage)
      return
    }
    setSkillText("")
    setSkills((currentSkills) => {
      if (currentSkills.includes(skill)) {
        setLocalErrorMessage("すでに登録されています")
        return currentSkills
      }
      return [...currentSkills, skill]
    })
  }, [])

  const deleteSkill = useCallback((skill: string) => {
    setSkills((currentSkills) => currentSkills.filter((s) => s !== skill))
  }, [])

  const onAddClick = useCallback(() => {
    addSkill(skillText)
  }, [skillText])

  useEffect(() => onChange?.(skills), [skills])

  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between">
        <Box flexGrow={1}>
          <InputAutoSuggest
            {...props}
            value={skillText}
            onChange={setSkillText}
            onSelectSuggestion={addSkill}
            error={!!errorMessage}
          />
        </Box>
        <Box flexShrink={0} w={96} ml={SpacingSize.XS}>
          <FunctionLink isButton size="Small" onClick={onAddClick}>
            追加する
          </FunctionLink>
        </Box>
      </Flex>

      {errorMessage && (
        <Box mt={SpacingSize.XS}>
          <ErrorText>{errorMessage}</ErrorText>
        </Box>
      )}

      <Box mt={SpacingSize.XS}>
        {isTagStyle ? (
          // タグ形式
          <Flex wrap="wrap" gridGap={SpacingSize.XS}>
            {skills.map((skill) => (
              <FormListEnteredItem key={skill} text={skill} onClickClose={() => deleteSkill(skill)} />
            ))}
          </Flex>
        ) : (
          // リスト形式(横いっぱい)
          <VStack spacing={SpacingSize.XS}>
            {skills.map((skill) => (
              <StyledFormListEnteredItem key={skill} text={skill} onClickClose={() => deleteSkill(skill)} />
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  )
}

const StyledFormListEnteredItem = styled(FormListEnteredItem)`
  width: 100%;
`
